import { DateTime } from "luxon";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useEffect } from "react";
import { Plus, Minus } from "lucide-react";
import type { SearchableSelectOption } from "../components/ui/SearchableSelect/SearchableSelect";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { FormField } from "../components/ui/FormField/FormField";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import { useCreateOffer, useUpdateOffer, useOfferDetail } from "../hooks/use-offers";
import { useStockItemUnits } from "../hooks/use-stock-items";
import { useFormDraft } from "../hooks/use-form-draft";
import { api } from "../lib/api";
import { useToast } from "../lib/use-toast";
import { formatAmount } from "../lib/date";
import { deriveGtip } from "@crm/shared/gtip";

const CURRENCY_OPTIONS = [
	{ value: "TL", label: "TL" },
	{ value: "USD", label: "USD" },
	{ value: "EUR", label: "EUR" },
	{ value: "GBP", label: "GBP" },
];
import styles from "./OfferNew.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/offers/new",
	component: withPermission(OfferNewPage, "offers", "create"),
});

interface LineItem {
	lineNumber: number;
	itemCode: string;
	itemDescription: string;
	displayDescription: string;
	quantity: string;
	unit: string;
	unitPrice: string;
	givenUnitPrice: string;
	vatRate: string;
	vatGroup: string;
	discountPercent: string;
	notes: string;
	stockItemId: string;
	showInPdf: boolean;
	showPriceInPdf: boolean;
	showDiscountInPdf: boolean;
	sendDatasheetInEmail: boolean;
	lineDetail: string;
	priceListModel: string;
	alternativeGroup: number;
}

const TERM_KEYS = [
	"commissioning",
	"assembly",
	"vat",
	"quality",
	"validity",
	"warranty",
	"payment",
	"delivery",
] as const;
type TermKey = (typeof TERM_KEYS)[number];

function OfferNewPage() {
	const search = route.useSearch() as {
		companyId?: string;
		contactPersonId?: string;
	};
	return <OfferFormPage initialSearch={search} />;
}

export function OfferFormPage({
	editId,
	initialSearch,
}: {
	editId?: string;
	initialSearch?: { companyId?: string; contactPersonId?: string };
}) {
	const { t, i18n } = useTranslation();
	const { showToast } = useToast();
	const navigate = useNavigate();
	const createOffer = useCreateOffer();
	const updateOffer = useUpdateOffer();
	const { data: unitsData } = useStockItemUnits();
	const unitOptions = (unitsData?.units ?? []).map((u) => ({ value: u, label: u }));
	const isEdit = !!editId;
	const detail = useOfferDetail(editId ?? "");
	const [prefilled, setPrefilled] = useState(false);
	const search = initialSearch ?? {};
	const [header, setHeader] = useState({
		validUntil: DateTime.now().plus({ days: 10 }).toISODate() ?? "",
		currency: "TL",
		paymentTerms: "",
		deliveryTerms: "",
		notes: "",
		offerDate: DateTime.now().toISODate() ?? "",
		commitmentDate: "",
		commitmentValue: "",
		commitmentUnit: "day",
		isTaxIncluded: false,
		pdfNotes: "",
		companyId: search.companyId ?? "",
		contactPersonId: search.contactPersonId ?? "",
	});
	const [termsLang, setTermsLang] = useState<"tr" | "en">(i18n.language === "en" ? "en" : "tr");
	const [termsI18n, setTermsI18n] = useState<Record<TermKey, { tr: string; en: string }>>({
		commissioning: { tr: "", en: "" },
		assembly: { tr: "", en: "" },
		vat: { tr: "", en: "" },
		quality: { tr: "", en: "" },
		validity: { tr: "", en: "" },
		warranty: { tr: "", en: "" },
		payment: { tr: "", en: "" },
		delivery: { tr: "", en: "" },
	});
	const setTermValue = (key: TermKey, value: string) => {
		setTermsI18n((prev) => ({ ...prev, [key]: { ...prev[key], [termsLang]: value } }));
	};
	const [initialOptions, setInitialOptions] = useState<
		Record<string, SearchableSelectOption | undefined>
	>({});
	const [formError, setFormError] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [selectedModelKey, setSelectedModelKey] = useState<Record<number, string>>({});
	const [modelInitialOptions, setModelInitialOptions] = useState<
		Record<number, SearchableSelectOption>
	>({});
	const [selectedSapKey, setSelectedSapKey] = useState<Record<number, string>>({});
	const [sapItemInitialOptions, setSapItemInitialOptions] = useState<
		Record<number, SearchableSelectOption>
	>({});
	// Seçilen modelin alternatör/kabin fiyatları (ürün adından bilgi türetmek için)
	const [linePrices, setLinePrices] = useState<
		Record<
			number,
			{
				alternators: Record<string, number>;
				cabins: Record<string, number>;
				currency: string | null;
			}
		>
	>({});
	const [lineInfo, setLineInfo] = useState<
		Record<number, { label: string; price: number; currency: string | null }[]>
	>({});
	const [appliedInfo, setAppliedInfo] = useState<Record<number, Record<string, boolean>>>({});
	const [appliedAmount, setAppliedAmount] = useState<Record<number, Record<string, number>>>({});
	const [groupRateDate, setGroupRateDate] = useState<Record<number, string>>({});
	const [groupRates, setGroupRates] = useState<Record<number, Record<string, number>>>({});
	const [vatGroups, setVatGroups] = useState<{ code: string; name: string; rate: number }[]>([]);
	useEffect(() => {
		void (async () => {
			try {
				const res =
					await api.get<{ code: string; name: string; rate: number }[]>(`/api/sap/vat-groups`);
				setVatGroups(res ?? []);
			} catch {
				setVatGroups([]);
			}
		})();
	}, []);
	const [lineWarning, setLineWarning] = useState<Record<number, string | null>>({});
	const [linePanels, setLinePanels] = useState<
		Record<number, Record<string, Record<string, number>>>
	>({});
	const [panelChecked, setPanelChecked] = useState<Record<number, Record<string, boolean>>>({});
	const [panelSelected, setPanelSelected] = useState<Record<number, Record<string, string>>>({});
	const [panelQty, setPanelQty] = useState<Record<number, Record<string, string>>>({});
	const [panelShow, setPanelShow] = useState<Record<number, Record<string, boolean>>>({});
	const [panelFolded, setPanelFolded] = useState<Record<number, Record<string, number>>>({});
	const [lineSource, setLineSource] = useState<Record<number, "priceList" | "sap">>({});
	const [directSapKey, setDirectSapKey] = useState<Record<number, string>>({});

	useEffect(() => {
		api
			.get<{ key: string; value: string | null; valueI18n: Record<string, string> | null }[]>(
				"/api/settings/offer-terms",
			)
			.then((rows) => {
				const map: Record<string, { tr: string; en: string }> = {};
				for (const row of rows) {
					if (row.key.startsWith("terms_")) {
						map[row.key] = {
							tr: row.valueI18n?.["tr"] ?? row.value ?? "",
							en: row.valueI18n?.["en"] ?? "",
						};
					}
				}
				setTermsI18n((prev) => ({
					commissioning: map["terms_startup"] ?? prev.commissioning,
					assembly: map["terms_installation"] ?? prev.assembly,
					vat: map["terms_vat"] ?? prev.vat,
					quality: map["terms_quality_standard"] ?? prev.quality,
					validity: map["terms_validity"] ?? prev.validity,
					warranty: map["terms_warranty"] ?? prev.warranty,
					payment: map["terms_payment"] ?? prev.payment,
					delivery: map["terms_delivery"] ?? prev.delivery,
				}));
			})
			.catch(() => {
				// leave empty defaults on error
			});
	}, []);

	useEffect(() => {
		const opts: Record<string, SearchableSelectOption> = {};
		const fetches: Promise<void>[] = [];
		if (header.companyId) {
			fetches.push(
				api
					.get<{ id: string; legalName: string }>(`/api/companies/${header.companyId}`)
					.then((c) => {
						opts.company = { value: c.id, label: c.legalName };
					})
					.catch(() => {}),
			);
		}
		if (header.contactPersonId) {
			fetches.push(
				api
					.get<{ id: string; firstName: string; lastName: string }>(
						`/api/persons/${header.contactPersonId}`,
					)
					.then((p) => {
						opts.person = { value: p.id, label: `${p.firstName} ${p.lastName}` };
					})
					.catch(() => {}),
			);
		}

		if (fetches.length === 0) return;
		Promise.all(fetches).then(() => setInitialOptions((prev) => ({ ...prev, ...opts })));
	}, [header.companyId, header.contactPersonId]);

	useEffect(() => {
		const companyId = header.companyId;
		if (!companyId || header.contactPersonId) return;
		let cancelled = false;
		api
			.get<{ items: { id: string; firstName: string; lastName: string; isPrimary: boolean }[] }>(
				`/api/persons?limit=1&companyId=${companyId}`,
			)
			.then((res) => {
				const primary = res.items[0];
				if (cancelled || !primary) return;
				setHeader((prev) =>
					prev.companyId === companyId && !prev.contactPersonId
						? { ...prev, contactPersonId: primary.id }
						: prev,
				);
				setInitialOptions((prev) => ({
					...prev,
					person: { value: primary.id, label: `${primary.firstName} ${primary.lastName}` },
				}));
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [header.companyId, header.contactPersonId]);
	const [lines, setLines] = useState<LineItem[]>([
		{
			lineNumber: 1,
			itemCode: "",
			itemDescription: "",
			displayDescription: "",
			quantity: "1",
			unit: "",
			unitPrice: "",
			givenUnitPrice: "",
			vatRate: "20",
			vatGroup: "",
			discountPercent: "0",
			notes: "",
			stockItemId: "",
			showInPdf: true,
			showPriceInPdf: true,
			showDiscountInPdf: false,
			sendDatasheetInEmail: true,
			lineDetail: "",
			priceListModel: "",
			alternativeGroup: 0,
		},
	]);

	interface OfferDraft {
		header: typeof header;
		termsI18n: typeof termsI18n;
		lines: typeof lines;
	}
	const draft = useFormDraft<OfferDraft>("offer-draft", !isEdit);

	const restoreDraft = () => {
		if (!draft.pendingDraft) return;
		setHeader(draft.pendingDraft.header);
		setTermsI18n(draft.pendingDraft.termsI18n);
		setLines(draft.pendingDraft.lines);
		const modelKeys: Record<number, string> = {};
		const modelOpts: Record<number, SearchableSelectOption> = {};
		const sapKeys: Record<number, string> = {};
		const sapOpts: Record<number, SearchableSelectOption> = {};
		draft.pendingDraft.lines.forEach((l, i) => {
			if (l.priceListModel) {
				modelKeys[i] = l.priceListModel;
				modelOpts[i] = { value: l.priceListModel, label: l.priceListModel };
			}
			if (l.stockItemId) {
				sapKeys[i] = l.stockItemId;
				sapOpts[i] = {
					value: l.stockItemId,
					label: `${l.itemCode ?? ""} — ${l.itemDescription ?? ""}`,
				};
			}
		});
		setSelectedModelKey(modelKeys);
		setModelInitialOptions(modelOpts);
		setSelectedSapKey(sapKeys);
		setSapItemInitialOptions(sapOpts);
		draft.acceptDraft();
	};

	useEffect(() => {
		if (draft.showPrompt) return;
		const hasData = !!(
			header.companyId ||
			header.contactPersonId ||
			header.notes ||
			header.pdfNotes ||
			lines.some((l) => l.itemCode || l.itemDescription || l.stockItemId || Number(l.unitPrice) > 0)
		);
		if (hasData) draft.save({ header, termsI18n, lines });
		else draft.clear();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [header, termsI18n, lines, draft.showPrompt]);

	useEffect(() => {
		if (!isEdit || prefilled || !detail.data) return;
		const o = detail.data;
		const d = (v: string | null) => (v ? v.slice(0, 10) : "");
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setHeader((prev) => ({
			...prev,
			validUntil: d(o.validUntil),
			currency: o.currency ?? prev.currency,
			paymentTerms: o.paymentTerms ?? "",
			deliveryTerms: o.deliveryTerms ?? "",
			notes: o.notes ?? "",
			offerDate: d(o.offerDate),
			commitmentDate: d(o.commitmentDate),
			commitmentValue: o.commitmentValue ?? "",
			commitmentUnit: o.commitmentUnit ?? "day",
			isTaxIncluded: o.isTaxIncluded ?? false,
			pdfNotes: o.pdfNotes ?? "",
			offerOrderDeliveryType: o.offerOrderDeliveryType ?? "",
			companyId: o.companyId ?? "",
			contactPersonId: o.contactPersonId ?? "",
		}));
		const legacyTerms: Record<TermKey, string | null> = {
			commissioning: o.termsCommissioning,
			assembly: o.termsAssembly,
			vat: o.termsVat,
			quality: o.termsQuality,
			validity: o.termsValidity,
			warranty: o.termsWarranty,
			payment: o.termsPayment,
			delivery: o.termsDelivery,
		};
		setTermsI18n((prev) => {
			const next = { ...prev };
			for (const key of TERM_KEYS) {
				const stored = o.termsI18n?.[key];
				next[key] = {
					tr: stored?.["tr"] ?? legacyTerms[key] ?? prev[key].tr ?? "",
					en: stored?.["en"] ?? prev[key].en ?? "",
				};
			}
			return next;
		});
		const sorted = [...(o.lines ?? [])].sort((a, b) => a.lineNumber - b.lineNumber);
		const modelKeys: Record<number, string> = {};
		const modelOpts: Record<number, SearchableSelectOption> = {};
		const sapKeys: Record<number, string> = {};
		const sapOpts: Record<number, SearchableSelectOption> = {};
		sorted.forEach((l, i) => {
			if (l.priceListModel) {
				modelKeys[i] = l.priceListModel;
				modelOpts[i] = { value: l.priceListModel, label: l.priceListModel };
			}
			if (l.stockItemId) {
				sapKeys[i] = l.stockItemId;
				sapOpts[i] = {
					value: l.stockItemId,
					label: `${l.itemCode ?? ""} — ${l.itemDescription ?? ""}`,
				};
			}
		});
		setSelectedModelKey(modelKeys);
		setModelInitialOptions(modelOpts);
		setSelectedSapKey(sapKeys);
		setSapItemInitialOptions(sapOpts);
		if (sorted.length > 0) {
			setLines(
				sorted.map((l, i) => ({
					lineNumber: i + 1,
					itemCode: l.itemCode ?? "",
					itemDescription: l.itemDescription ?? "",
					displayDescription: l.displayDescription ?? "",
					quantity: l.quantity ?? "1",
					unit: l.unit ?? "",
					unitPrice: l.unitPrice ?? "",
					givenUnitPrice: l.givenUnitPrice ?? "",
					vatRate: l.vatRate ?? "20",
					vatGroup: l.vatGroup ?? "",
					discountPercent: l.discountPercent ?? "0",
					notes: l.notes ?? "",
					stockItemId: l.stockItemId ?? "",
					showInPdf: l.showInPdf ?? true,
					showPriceInPdf: l.showPriceInPdf ?? true,
					showDiscountInPdf: l.showDiscountInPdf ?? false,
					sendDatasheetInEmail: l.sendDatasheetInEmail ?? true,
					lineDetail: l.lineDetail ?? "",
					priceListModel: l.priceListModel ?? "",
					alternativeGroup: l.alternativeGroup ?? 0,
				})),
			);
		}
		setPrefilled(true);
	}, [isEdit, prefilled, detail.data]);

	const addLine = (group = 0) => {
		setLines([
			...lines,
			{
				lineNumber: lines.length + 1,
				itemCode: "",
				itemDescription: "",
				displayDescription: "",
				quantity: "1",
				unit: "",
				unitPrice: "",
				givenUnitPrice: "",
				vatRate: "20",
				vatGroup: "",
				discountPercent: "0",
				notes: "",
				stockItemId: "",
				showInPdf: true,
				showPriceInPdf: true,
				showDiscountInPdf: false,
				sendDatasheetInEmail: true,
				lineDetail: "",
				priceListModel: "",
				alternativeGroup: group,
			},
		]);
	};

	const [groupCurrency, setGroupCurrency] = useState<Record<number, string>>({});
	const alternativeGroups = [...new Set(lines.map((l) => l.alternativeGroup))].sort(
		(a, b) => a - b,
	);
	const currencyOfGroup = (group: number) => groupCurrency[group] ?? header.currency;

	const LOCAL_CURRENCIES = new Set(["TL", "TRY"]);
	// 1 birim dövizin yerel para karşılığı; yerel para için 1
	const rateFor = (group: number, currency: string | null | undefined): number | null => {
		if (!currency) return null;
		if (LOCAL_CURRENCIES.has(currency)) return 1;
		const r = groupRates[group]?.[currency];
		return r && r > 0 ? r : null;
	};
	const convertPrice = (
		price: number,
		fromCurrency: string | null | undefined,
		group: number,
	): { value: number; ok: boolean; converted: boolean } => {
		const target = currencyOfGroup(group);
		if (!fromCurrency || fromCurrency === target)
			return { value: price, ok: true, converted: false };
		const rf = rateFor(group, fromCurrency);
		const rt = rateFor(group, target);
		if (rf == null || rt == null) return { value: price, ok: false, converted: false };
		const value = Math.round(((price * rf) / rt) * 100) / 100;
		return { value, ok: true, converted: true };
	};

	const fetchGroupRates = async (group: number, date: string) => {
		setGroupRateDate((prev) => ({ ...prev, [group]: date }));
		if (!date) return;
		try {
			const rows = await api.get<Array<{ currency: string; rate: number }>>(
				`/api/sap/currency-rates?date=${date}`,
			);
			const map: Record<string, number> = {};
			for (const r of rows) map[r.currency] = r.rate;
			setGroupRates((prev) => ({ ...prev, [group]: map }));
			recomputeGroupApplied(group, currencyOfGroup(group), map);
		} catch {
			setGroupRates((prev) => ({ ...prev, [group]: {} }));
		}
	};

	const recomputeGroupApplied = (
		group: number,
		target: string,
		ratesMap: Record<string, number>,
	) => {
		const rateOf = (currency: string | null | undefined): number | null => {
			if (!currency) return null;
			if (LOCAL_CURRENCIES.has(currency)) return 1;
			const r = ratesMap[currency];
			return r && r > 0 ? r : null;
		};
		const nextAmounts: Record<number, Record<string, number>> = { ...appliedAmount };
		setLines((prev) =>
			prev.map((line, idx) => {
				if (line.alternativeGroup !== group) return line;
				const appliedLabels = Object.keys(appliedInfo[idx] ?? {}).filter(
					(l) => appliedInfo[idx]?.[l],
				);
				if (appliedLabels.length === 0) return line;
				let base = Number(line.unitPrice) || 0;
				for (const label of appliedLabels) base -= appliedAmount[idx]?.[label] ?? 0;
				const newAmounts: Record<string, number> = {};
				for (const label of appliedLabels) {
					const info = lineInfo[idx]?.find((i) => i.label === label);
					const price = info?.price ?? 0;
					const from = info?.currency ?? null;
					let value = price;
					if (from && from !== target) {
						const rf = rateOf(from);
						const rt = rateOf(target);
						if (rf != null && rt != null) value = Math.round(((price * rf) / rt) * 100) / 100;
					}
					newAmounts[label] = value;
					base += value;
				}
				nextAmounts[idx] = newAmounts;
				const newList = Math.max(0, Math.round(base * 100) / 100);
				const given = line.givenUnitPrice;
				const disc =
					given && given.trim() !== ""
						? String(computeDiscount(newList, Number(given) || 0))
						: line.discountPercent;
				return { ...line, unitPrice: String(newList), discountPercent: disc } as LineItem;
			}),
		);
		setAppliedAmount(nextAmounts);
	};

	const changeGroupCurrency = (group: number, target: string) => {
		setGroupCurrency((prev) => ({ ...prev, [group]: target }));
		recomputeGroupApplied(group, target, groupRates[group] ?? {});
	};

	const addAlternative = () => {
		const nextGroup = (alternativeGroups[alternativeGroups.length - 1] ?? 0) + 1;
		setGroupCurrency((prev) => ({ ...prev, [nextGroup]: header.currency }));
		addLine(nextGroup);
	};

	const removeAlternative = (group: number) => {
		setLines((prev) =>
			prev.filter((l) => l.alternativeGroup !== group).map((l, i) => ({ ...l, lineNumber: i + 1 })),
		);
	};

	// Net birim fiyat: verilen fiyat girildiyse doğrudan o (yuvarlama sapması olmaz),
	// yoksa liste × (1 − iskonto).
	const netUnitPrice = (l: LineItem): number => {
		const given = l.givenUnitPrice?.trim();
		if (given) return Number(given) || 0;
		return (Number(l.unitPrice) || 0) * (1 - (Number(l.discountPercent) || 0) / 100);
	};

	const groupTotals = (group: number) => {
		const groupLines = lines.filter((l) => l.alternativeGroup === group);
		const subtotal = groupLines.reduce(
			(sum, l) => sum + (Number(l.quantity) || 0) * netUnitPrice(l),
			0,
		);
		const vatTotal = groupLines.reduce(
			(sum, l) =>
				sum + (Number(l.quantity) || 0) * netUnitPrice(l) * ((Number(l.vatRate) || 0) / 100),
			0,
		);
		return { subtotal, vatTotal, total: subtotal + vatTotal };
	};

	const removeLine = (idx: number) => {
		setLines(lines.filter((_, i) => i !== idx).map((l, i) => ({ ...l, lineNumber: i + 1 })));
	};

	const updateLine = (idx: number, field: keyof LineItem, value: string) => {
		const next = [...lines];
		next[idx] = { ...next[idx], [field]: value } as LineItem;
		setLines(next);
	};

	const computeDiscount = (listPrice: number, given: number): number => {
		if (!(listPrice > 0)) return 0;
		return Math.round((1 - given / listPrice) * 10000) / 100;
	};

	const setGivenPrice = (idx: number, value: string) => {
		setLines((prev) => {
			const line = prev[idx];
			if (!line) return prev;
			const list = Number(line.unitPrice) || 0;
			const next = [...prev];
			if (value.trim() === "") {
				next[idx] = { ...line, givenUnitPrice: "", discountPercent: "0" } as LineItem;
			} else {
				const disc = computeDiscount(list, Number(value) || 0);
				next[idx] = { ...line, givenUnitPrice: value, discountPercent: String(disc) } as LineItem;
			}
			return next;
		});
	};

	const setDiscountPercent = (idx: number, value: string) => {
		setLines((prev) => {
			const line = prev[idx];
			if (!line) return prev;
			const next = [...prev];
			next[idx] = {
				...line,
				discountPercent: value.trim() === "" ? "0" : value,
				givenUnitPrice: "",
			} as LineItem;
			return next;
		});
	};

	const syncDiscount = (idx: number) => {
		setLines((prev) => {
			const line = prev[idx];
			if (!line) return prev;
			const given = line.givenUnitPrice;
			if (!given || given.trim() === "") return prev;
			const disc = computeDiscount(Number(line.unitPrice) || 0, Number(given) || 0);
			const next = [...prev];
			next[idx] = { ...line, discountPercent: String(disc) } as LineItem;
			return next;
		});
	};

	const toggleLineFlag = (idx: number, flag: keyof LineItem) => {
		const next = [...lines];
		const line = next[idx]!;
		next[idx] = { ...line, [flag]: !line[flag] } as LineItem;
		setLines(next);
	};

	const calcLineTotal = (line: LineItem) => {
		const qty = Number(line.quantity) || 0;
		const vat = Number(line.vatRate) || 0;
		const base = qty * netUnitPrice(line);
		return base * (1 + vat / 100);
	};

	const buildPriceComponents = (idx: number) => {
		const components: { label: string; price: number }[] = [];
		for (const info of lineInfo[idx] ?? []) {
			if (appliedInfo[idx]?.[info.label]) components.push({ label: info.label, price: info.price });
		}
		for (const group of ["ATS", "Change Over ATS", "CB"]) {
			if (!(panelChecked[idx]?.[group] ?? false)) continue;
			const option = panelSelected[idx]?.[group] ?? "";
			if (!option) continue;
			const qty = Number(panelQty[idx]?.[group] ?? "1") || 0;
			const price = linePanels[idx]?.[group]?.[option] ?? 0;
			if (qty <= 0) continue;
			components.push({
				label: qty > 1 ? `${group} - ${option} x${qty}` : `${group} - ${option}`,
				price: price * qty,
			});
		}
		return components;
	};

	const buildPanelLines = (idx: number) => {
		const parent = lines[idx];
		if (!parent) return [];
		const groups = Object.keys(linePanels[idx] ?? {});
		const result: Array<{
			itemDescription: string;
			quantity: number;
			unit: string;
			unitPrice: number;
			vatRate: number;
			showPriceInPdf: boolean;
		}> = [];
		for (const group of ["ATS", "Change Over ATS", "CB"]) {
			if (!groups.includes(group)) continue;
			if (!(panelChecked[idx]?.[group] ?? false)) continue;
			if (!(panelShow[idx]?.[group] ?? true)) continue;
			const option = panelSelected[idx]?.[group] ?? "";
			if (!option) continue;
			const qty = Number(panelQty[idx]?.[group] ?? "1") || 0;
			if (qty <= 0) continue;
			result.push({
				itemDescription: `${group} - ${option}`,
				quantity: qty,
				unit: "Adet",
				unitPrice: 0,
				vatRate: Number(parent.vatRate) || 0,
				showPriceInPdf: false,
			});
		}
		return result;
	};

	const subtotal = lines.reduce((sum, l) => sum + (Number(l.quantity) || 0) * netUnitPrice(l), 0);
	const vatTotal = lines.reduce(
		(sum, l) =>
			sum + (Number(l.quantity) || 0) * netUnitPrice(l) * ((Number(l.vatRate) || 0) / 100),
		0,
	);
	const total = subtotal + vatTotal;

	const loadCompanies = useCallback(async (query: string) => {
		const res = await api.get<{
			items: Array<{ id: string; legalName: string; currency: string | null }>;
		}>(
			`/api/companies?limit=50&status=customer${query ? `&search=${encodeURIComponent(query)}` : ""}`,
		);
		return res.items.map((c) => ({
			value: c.id,
			label: `${c.legalName}${c.currency ? ` (${c.currency})` : ""}`,
		}));
	}, []);

	const loadPersons = useCallback(
		async (query: string) => {
			const res = await api.get<{
				items: Array<{ id: string; firstName: string; lastName: string }>;
			}>(
				`/api/persons?limit=50${header.companyId ? `&companyId=${header.companyId}` : ""}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}` }));
		},
		[header.companyId],
	);

	// Adım 1: Fiyat listesinden MODEL ara (varyant açmadan, her model tek satır).
	const loadPriceModels = useCallback(async (query: string) => {
		const rows = await api.get<
			Array<{ model: string; standbyKva: string | null; motorModel: string | null }>
		>(`/api/price-list?limit=50${query ? `&search=${encodeURIComponent(query)}` : ""}`);
		return rows.map((r) => ({
			value: r.model,
			label: `${r.model}${r.standbyKva ? ` — ${r.standbyKva} kVA` : ""}${r.motorModel ? ` (${r.motorModel})` : ""}`,
		}));
	}, []);

	// Model seçilince: o modelin alternatör/kabin fiyatlarını yükle, SAP ürün seçimini sıfırla.
	const handleSelectModel = async (idx: number, model: string) => {
		setSelectedModelKey((prev) => ({ ...prev, [idx]: model }));
		setSelectedSapKey((prev) => ({ ...prev, [idx]: "" }));
		setLineInfo((prev) => ({ ...prev, [idx]: [] }));
		setLineWarning((prev) => ({ ...prev, [idx]: null }));
		setPanelChecked((prev) => ({ ...prev, [idx]: {} }));
		setPanelSelected((prev) => ({ ...prev, [idx]: {} }));
		setPanelQty((prev) => ({ ...prev, [idx]: {} }));
		setPanelShow((prev) => ({ ...prev, [idx]: {} }));
		setPanelFolded((prev) => ({ ...prev, [idx]: {} }));
		const next = [...lines];
		const line = next[idx];
		if (line) {
			next[idx] = { ...line, itemCode: model, priceListModel: model, stockItemId: "" } as LineItem;
			setLines(next);
		}
		if (!model) {
			setLinePrices((prev) => ({
				...prev,
				[idx]: { alternators: {}, cabins: {}, currency: null },
			}));
			setLinePanels((prev) => ({ ...prev, [idx]: {} }));
			return;
		}
		try {
			const row = await api.get<{
				alternators: Record<string, number>;
				cabins: Record<string, number>;
				panels: Record<string, Record<string, number>>;
				currency: string | null;
			} | null>(`/api/price-list/lookup?model=${encodeURIComponent(model)}`);
			setLinePrices((prev) => ({
				...prev,
				[idx]: {
					alternators: row?.alternators ?? {},
					cabins: row?.cabins ?? {},
					currency: row?.currency ?? null,
				},
			}));
			setLinePanels((prev) => ({ ...prev, [idx]: row?.panels ?? {} }));
			if (row?.currency && row.currency !== header.currency) {
				setHeader((prev) => ({ ...prev, currency: row.currency as string }));
			}
		} catch {
			setLinePrices((prev) => ({
				...prev,
				[idx]: { alternators: {}, cabins: {}, currency: null },
			}));
			setLinePanels((prev) => ({ ...prev, [idx]: {} }));
		}
	};

	const panelLineAmount = (idx: number, group: string, option: string, qty: string) => {
		const price = option ? (linePanels[idx]?.[group]?.[option] ?? 0) : 0;
		return price * (Number(qty) || 0);
	};

	const syncPanelFold = (idx: number, group: string, option: string, qty: string) => {
		const desired = panelLineAmount(idx, group, option, qty);
		const current = panelFolded[idx]?.[group] ?? 0;
		const delta = desired - current;
		if (delta !== 0) {
			const cur = Number(lines[idx]?.unitPrice) || 0;
			updateLine(idx, "unitPrice", String(Math.max(0, Math.round((cur + delta) * 100) / 100)));
			syncDiscount(idx);
			setPanelFolded((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: desired } }));
		}
	};

	const togglePanelGroup = (idx: number, group: string) => {
		const checked = panelChecked[idx]?.[group] ?? false;
		if (checked) {
			syncPanelFold(idx, group, "", panelQty[idx]?.[group] ?? "1");
			setPanelSelected((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: "" } }));
		} else {
			setPanelQty((prev) => ({
				...prev,
				[idx]: { ...prev[idx], [group]: prev[idx]?.[group] ?? "1" },
			}));
			setPanelShow((prev) => ({
				...prev,
				[idx]: { ...prev[idx], [group]: prev[idx]?.[group] ?? true },
			}));
		}
		setPanelChecked((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: !checked } }));
	};

	const selectPanelOption = (idx: number, group: string, option: string) => {
		const qty = panelQty[idx]?.[group] ?? "1";
		setPanelSelected((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: option } }));
		syncPanelFold(idx, group, option, qty);
	};

	const setPanelQtyValue = (idx: number, group: string, qty: string) => {
		const option = panelSelected[idx]?.[group] ?? "";
		setPanelQty((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: qty } }));
		syncPanelFold(idx, group, option, qty);
	};

	const togglePanelShow = (idx: number, group: string) => {
		const show = !(panelShow[idx]?.[group] ?? true);
		setPanelShow((prev) => ({ ...prev, [idx]: { ...prev[idx], [group]: show } }));
	};

	// Türkçe normalize + tekrar eden harfleri sadeleştir (MARANELLO ≈ MARANELO eşleşsin)
	const norm = (s: string) =>
		s
			.toLocaleUpperCase("tr-TR")
			.replace(/\s+/g, "")
			.replace(/(.)\1+/g, "$1");

	// Ürün adından alternatör + kabin tespit et; eşleşen fiyatları ve (varsa) uyarıyı döndür
	const deriveFromName = (name: string, idx: number) => {
		const row = linePrices[idx];
		const info: { label: string; price: number; currency: string | null }[] = [];
		let warning: string | null = null;
		if (!row) return { info, warning };
		const up = name.toLocaleUpperCase("tr-TR");
		const nameNorm = norm(name);

		// Alternatör: sütun adının ilk kelimesi ürün adında geçiyor mu?
		const altEntry = Object.entries(row.alternators).find(([col]) => {
			const firstWord = col.split(/\s+/)[0] ?? col;
			return nameNorm.includes(norm(firstWord));
		});
		if (altEntry) {
			info.push({ label: altEntry[0], price: altEntry[1], currency: row.currency });
		} else {
			// Üründe alternatör markası belirtilmiş ama listede yok → uyarı
			const m = up.match(/([A-ZÇĞİÖŞÜ]+)\s+ALTERNAT/);
			const brand = m?.[1];
			if (brand) warning = `${brand} ${t("alternatorNotInList")}`;
		}

		// Kabin: süper sessiz / kabinli / kabinsiz
		const cabinEntries = Object.entries(row.cabins);
		const findCabin = (test: (c: string) => boolean) =>
			cabinEntries.find(([c]) => test(c.toLocaleUpperCase("tr-TR")));
		let cabin: [string, number] | undefined;
		if (up.includes("SÜPER SESSİZ") || up.includes("SUPER SESSIZ"))
			cabin = findCabin((c) => c.includes("SESS"));
		else if (up.includes("KABİNLİ") || up.includes("KABINLI"))
			cabin = findCabin((c) => c.includes("KAB") && !c.includes("SESS"));
		if (cabin) info.push({ label: cabin[0], price: cabin[1], currency: row.currency });

		return { info, warning };
	};

	const DIRECT_SAP_ITEM_GROUPS = "100,103,105,106,108";

	const loadDirectSapItems = useCallback(async (query: string) => {
		const res = await api.get<{
			items: Array<{
				id: string;
				code: string;
				name: string;
				uom: string | null;
				salesVatGroup: string | null;
			}>;
		}>(
			`/api/stock-items?limit=50&activeOnly=true&itemGroups=${DIRECT_SAP_ITEM_GROUPS}${
				query ? `&search=${encodeURIComponent(query)}` : ""
			}`,
		);
		return res.items.map((s) => ({
			value: s.id,
			label: `${s.code} — ${s.name}`,
			meta: { code: s.code, name: s.name, uom: s.uom ?? "", vatGroup: s.salesVatGroup ?? "" },
		}));
	}, []);

	// Ürünün SAP satış KDV grubunu satıra varsayılan uygula (oran da gruptan)
	const applyDefaultVatGroup = (line: LineItem, vatGroup: string | undefined): LineItem => {
		if (!vatGroup) return line;
		const g = vatGroups.find((v) => v.code === vatGroup);
		return { ...line, vatGroup, ...(g ? { vatRate: String(g.rate) } : {}) };
	};

	const handleSelectDirectSapItem = (
		idx: number,
		stockItemId: string,
		option?: { label: string; meta?: Record<string, string> },
	) => {
		setDirectSapKey((prev) => ({ ...prev, [idx]: stockItemId }));
		if (!option?.meta) return;
		const { code, name, uom, vatGroup } = option.meta;
		setLines((prev) => {
			const next = [...prev];
			const line = next[idx];
			if (!line) return prev;
			next[idx] = applyDefaultVatGroup(
				{
					...line,
					stockItemId,
					itemCode: code ?? line.itemCode,
					itemDescription: name ?? line.itemDescription,
					unit: uom || line.unit,
					priceListModel: "",
				} as LineItem,
				vatGroup,
			);
			return next;
		});
	};

	const changeLineSource = (idx: number, source: "priceList" | "sap") => {
		setLineSource((prev) => ({ ...prev, [idx]: source }));
		setSelectedModelKey((prev) => ({ ...prev, [idx]: "" }));
		setSelectedSapKey((prev) => ({ ...prev, [idx]: "" }));
		setDirectSapKey((prev) => ({ ...prev, [idx]: "" }));
		setLineInfo((prev) => ({ ...prev, [idx]: [] }));
		setAppliedInfo((prev) => ({ ...prev, [idx]: {} }));
		setLineWarning((prev) => ({ ...prev, [idx]: null }));
		setLinePanels((prev) => ({ ...prev, [idx]: {} }));
		setPanelChecked((prev) => ({ ...prev, [idx]: {} }));
		setPanelSelected((prev) => ({ ...prev, [idx]: {} }));
		setPanelFolded((prev) => ({ ...prev, [idx]: {} }));
		setLines((prev) => {
			const next = [...prev];
			const line = next[idx];
			if (!line) return prev;
			next[idx] = {
				...line,
				itemCode: "",
				itemDescription: "",
				stockItemId: "",
				priceListModel: "",
				unitPrice: "",
				givenUnitPrice: "",
				discountPercent: "0",
			} as LineItem;
			return next;
		});
	};

	// Adım 2: Seçilen modeli içeren SAP stok kalemlerini LIKE ile getir.
	const loadSapItemsForModel = useCallback(async (model: string, query: string) => {
		// stok araması tek terim üzerinden çalışır; modelle daralt, kullanıcı yazarsa onu kullan
		const typed = query.trim();
		const search = typed || model;
		const res = await api.get<{
			items: Array<{
				id: string;
				code: string;
				name: string;
				uom: string | null;
				salesVatGroup: string | null;
			}>;
		}>(`/api/stock-items?limit=200${search ? `&search=${encodeURIComponent(search)}` : ""}`);
		let items = res.items;
		// Kullanıcı serbest arama yapmadıysa: modeli sınırla (GJP10 → GJP10-..., GJP100 hariç)
		if (!typed && model) {
			const esc = model.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
			// isim modelle başlamalı ve sonrasında rakam/harf gelmemeli (GJP10- ✓, GJP100 ✗)
			const re = new RegExp(`^${esc}([^0-9A-Za-z]|$)`, "i");
			const filtered = items.filter((s) => re.test(s.name.trim()));
			if (filtered.length > 0) items = filtered;
		}
		return items.map((s) => ({
			value: s.id,
			label: `${s.code} — ${s.name}`,
			meta: { code: s.code, name: s.name, uom: s.uom ?? "", vatGroup: s.salesVatGroup ?? "" },
		}));
	}, []);

	const handleSelectSapItem = (
		idx: number,
		stockItemId: string,
		option?: { label: string; meta?: Record<string, string> },
	) => {
		setSelectedSapKey((prev) => ({ ...prev, [idx]: stockItemId }));
		if (!option?.meta) return;
		const { code, name, uom, vatGroup } = option.meta;
		const next = [...lines];
		const line = next[idx];
		if (!line) return;
		// Alternatör + kabin fiyatlarını otomatik hesapla ve birim fiyata ekle
		const { info, warning } = deriveFromName(name ?? "", idx);
		const autoTotal = info.reduce((sum, i) => sum + i.price, 0);
		const applied: Record<string, boolean> = {};
		info.forEach((i) => (applied[i.label] = true));
		next[idx] = applyDefaultVatGroup(
			{
				...line,
				stockItemId,
				itemCode: code ?? line.itemCode,
				itemDescription: name ?? line.itemDescription,
				unit: uom || line.unit,
				unitPrice: autoTotal > 0 ? String(Math.round(autoTotal * 100) / 100) : line.unitPrice,
				givenUnitPrice: "",
				discountPercent: "0",
			} as LineItem,
			vatGroup,
		);
		setLines(next);
		setLineInfo((prev) => ({ ...prev, [idx]: info }));
		setAppliedInfo((prev) => ({ ...prev, [idx]: applied }));
		setLineWarning((prev) => ({ ...prev, [idx]: warning }));
	};

	// Bilgi rozetini birim fiyata ekle / çıkar (toggle)
	const toggleInfoPrice = (
		idx: number,
		info: { label: string; price: number; currency?: string | null },
	) => {
		const isApplied = appliedInfo[idx]?.[info.label] ?? false;
		const group = lines[idx]?.alternativeGroup ?? 0;
		const current = Number(lines[idx]?.unitPrice) || 0;
		if (isApplied) {
			// Kaldır: eklenirken kaydedilen gerçek tutarı düş
			const applied = appliedAmount[idx]?.[info.label] ?? info.price;
			updateLine(
				idx,
				"unitPrice",
				String(Math.max(0, Math.round((current - applied) * 100) / 100)),
			);
			setAppliedAmount((prev) => {
				const next = { ...(prev[idx] ?? {}) };
				delete next[info.label];
				return { ...prev, [idx]: next };
			});
		} else {
			const conv = convertPrice(info.price, info.currency ?? null, group);
			if (!conv.ok) {
				setLineWarning((prev) => ({ ...prev, [idx]: t("noRatesForDate") }));
			}
			updateLine(
				idx,
				"unitPrice",
				String(Math.max(0, Math.round((current + conv.value) * 100) / 100)),
			);
			setAppliedAmount((prev) => ({
				...prev,
				[idx]: { ...prev[idx], [info.label]: conv.value },
			}));
		}
		setAppliedInfo((prev) => ({
			...prev,
			[idx]: { ...prev[idx], [info.label]: !isApplied },
		}));
		syncDiscount(idx);
	};

	const submit = () => {
		setFormError("");
		const errors: Record<string, string> = {};
		if (!header.companyId) errors.companyId = t("requiredField");
		if (!header.offerDate) errors.offerDate = t("requiredField");
		if (!header.validUntil) errors.validUntil = t("requiredField");
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) {
			showToast(t("fillRequiredFields"), "error");
			return;
		}
		const invalidLine = lines.findIndex((l) => !l.quantity || Number(l.quantity) <= 0);
		if (invalidLine !== -1) {
			setFormError(t("fillRequiredFields"));
			showToast(t("fillRequiredFields"), "error");
			return;
		}
		const payload = {
			...header,
			validUntil: header.validUntil || undefined,
			offerDate: header.offerDate || undefined,
			commitmentDate: header.commitmentDate || undefined,
			commitmentValue: header.commitmentValue || undefined,
			commitmentUnit: header.commitmentValue ? header.commitmentUnit : undefined,
			isTaxIncluded: header.isTaxIncluded,
			pdfNotes: isEdit ? header.pdfNotes : header.pdfNotes || undefined,
			// legacy single-language columns kept in sync (tr) for SAP outbound sync
			termsCommissioning: termsI18n.commissioning?.tr || undefined,
			termsAssembly: termsI18n.assembly?.tr || undefined,
			termsVat: termsI18n.vat?.tr || undefined,
			termsQuality: termsI18n.quality?.tr || undefined,
			termsValidity: termsI18n.validity?.tr || undefined,
			termsWarranty: termsI18n.warranty?.tr || undefined,
			termsPayment: termsI18n.payment?.tr || undefined,
			termsDelivery: termsI18n.delivery?.tr || undefined,
			termsI18n,
			companyId: header.companyId || undefined,
			contactPersonId: header.contactPersonId || undefined,
			lines: lines
				.flatMap((l, idx) => {
					const parentLine = {
						itemCode: l.itemCode || undefined,
						itemDescription: l.itemDescription || undefined,
						displayDescription: l.displayDescription || undefined,
						quantity: Number(l.quantity) || 1,
						unit: l.unit || undefined,
						unitPrice: Number(l.unitPrice) || 0,
						givenUnitPrice: l.givenUnitPrice ? Number(l.givenUnitPrice) : undefined,
						vatRate: l.vatRate ? Number(l.vatRate) : undefined,
						vatGroup: l.vatGroup || undefined,
						discountPercent: l.discountPercent ? Number(l.discountPercent) : undefined,
						lineTotal: calcLineTotal(l),
						notes: l.notes || undefined,
						priceListModel: l.priceListModel || selectedModelKey[idx] || undefined,
						stockItemId: l.stockItemId || undefined,
						showInPdf: l.showInPdf,
						showPriceInPdf: l.showPriceInPdf,
						showDiscountInPdf: l.showDiscountInPdf,
						sendDatasheetInEmail: l.sendDatasheetInEmail,
						lineDetail: l.lineDetail || undefined,
						alternativeGroup: l.alternativeGroup,
						alternativeCurrency: currencyOfGroup(l.alternativeGroup),
						priceComponents: buildPriceComponents(idx).length
							? buildPriceComponents(idx)
							: undefined,
					};
					const panelLines = buildPanelLines(idx).map((p) => ({
						itemCode: undefined,
						itemDescription: p.itemDescription,
						displayDescription: p.itemDescription,
						quantity: p.quantity,
						unit: p.unit,
						unitPrice: p.unitPrice,
						vatRate: p.vatRate || undefined,
						discountPercent: undefined,
						lineTotal: p.quantity * p.unitPrice * (1 + p.vatRate / 100),
						notes: undefined,
						priceListModel: undefined,
						stockItemId: undefined,
						showInPdf: true,
						showPriceInPdf: p.showPriceInPdf,
						lineDetail: undefined,
						alternativeGroup: l.alternativeGroup,
						alternativeCurrency: currencyOfGroup(l.alternativeGroup),
					}));
					return [parentLine, ...panelLines];
				})
				.map((line, i) => ({ ...line, lineNumber: i + 1 })),
		};

		const onError = (err: unknown) => {
			const message = err instanceof Error ? err.message : t("error");
			setFormError(message);
			showToast(message, "error");
		};

		if (isEdit && editId) {
			updateOffer.mutate(
				{ id: editId, data: payload },
				{
					onSuccess: (updated) => {
						alert(t("updateSuccess"));
						navigate({ to: "/offers/$id", params: { id: updated.id } });
					},
					onError,
				},
			);
		} else {
			createOffer.mutate(payload, {
				onSuccess: () => {
					draft.clear();
					alert(t("createSuccess"));
					navigate({
						to: "/offers",
						search: {
							page: 1,
							size: 20,
							status: "",
							search: "",
							companyId: undefined,
							assignedTo: undefined,
							offerDateFrom: "",
							offerDateTo: "",
							sort: "",
							dir: "desc" as const,
						},
					});
				},
				onError,
			});
		}
	};

	return (
		<div>
			<PageHeader title={isEdit ? t("editOffer") : t("createOffer")} />

			{draft.showPrompt && (
				<Card>
					<div className={styles.draftPrompt}>
						<span>Kaydedilmemiş bir taslak bulundu. Verileriniz geri yüklensin mi?</span>
						<div className={styles.draftPromptActions}>
							<Button onClick={restoreDraft}>Evet</Button>
							<Button variant="ghost" onClick={draft.discardDraft}>
								Hayır
							</Button>
						</div>
					</div>
				</Card>
			)}

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.formGrid}>
						{formError && <div className={styles.formError}>{formError}</div>}
						<FormField label={t("company")} required>
							<SearchableSelect
								value={header.companyId}
								onChange={(val) => {
									setHeader({ ...header, companyId: val, contactPersonId: "" });
									setInitialOptions((prev) => ({
										...prev,
										person: undefined,
									}));
								}}
								loadOptions={loadCompanies}
								initialOption={initialOptions.company}
								placeholder={t("selectCompany")}
								clearable
							/>
						</FormField>
						<FormField label={t("contactPersons")}>
							<SearchableSelect
								value={header.contactPersonId}
								onChange={(val) => setHeader({ ...header, contactPersonId: val })}
								loadOptions={loadPersons}
								initialOption={initialOptions.person}
								placeholder={t("selectPerson")}
								disabled
							/>
						</FormField>
						<FormField label={t("validUntil")} required>
							<Input
								type="date"
								value={header.validUntil}
								onChange={(e) => {
									const days = e.target.value
										? Math.round(
												DateTime.fromISO(e.target.value).diff(DateTime.now().startOf("day"), "days")
													.days,
											)
										: null;
									setHeader({ ...header, validUntil: e.target.value });
									if (days !== null && days > 0) {
										setTermsI18n((prev) => ({
											...prev,
											validity: {
												...prev.validity,
												tr: `Fiyatlarımız ${days} gün için geçerlidir.`,
											},
										}));
									}
								}}
								error={fieldErrors.validUntil}
							/>
						</FormField>
						<FormField label={t("currency")}>
							<Select
								options={CURRENCY_OPTIONS}
								value={header.currency}
								onChange={(e) => setHeader({ ...header, currency: e.target.value })}
							/>
						</FormField>
						<FormField label={t("offerDate")} required>
							<Input
								type="date"
								value={header.offerDate}
								onChange={(e) => setHeader({ ...header, offerDate: e.target.value })}
								error={fieldErrors.offerDate}
							/>
						</FormField>
						<FormField label={t("commitmentDuration")}>
							<div className={styles.durationRow}>
								<Input
									type="text"
									placeholder="2-4"
									value={header.commitmentValue}
									onChange={(e) => setHeader({ ...header, commitmentValue: e.target.value })}
								/>
								<Select
									options={[
										{ value: "day", label: t("unitDay") },
										{ value: "week", label: t("unitWeek") },
										{ value: "month", label: t("unitMonth") },
									]}
									value={header.commitmentUnit}
									onChange={(e) => setHeader({ ...header, commitmentUnit: e.target.value })}
								/>
							</div>
						</FormField>
						<FormField label={t("factoryNotes")}>
							<Input
								value={header.notes}
								onChange={(e) => setHeader({ ...header, notes: e.target.value })}
							/>
						</FormField>
						<FormField label={t("pdfNotes")}>
							<Input
								value={header.pdfNotes}
								onChange={(e) => setHeader({ ...header, pdfNotes: e.target.value })}
							/>
						</FormField>
						<div className={styles.checkboxFieldWrapper}>
							<label className={styles.checkboxField}>
								<input
									type="checkbox"
									checked={header.isTaxIncluded}
									onChange={(e) => {
										setHeader({ ...header, isTaxIncluded: e.target.checked });
										setTermsI18n((prev) => ({
											...prev,
											vat: {
												...prev.vat,
												tr: e.target.checked ? "Fiyata dahildir." : "Fiyata dahil değildir.",
											},
										}));
									}}
								/>
								<span>{t("isTaxIncluded")}</span>
							</label>
						</div>
					</div>
				</Card>

				<Card header={t("terms")}>
					<div className={styles.termsLangRow}>
						<button
							type="button"
							className={[styles.langButton, termsLang === "tr" && styles.langButtonActive]
								.filter(Boolean)
								.join(" ")}
							onClick={() => setTermsLang("tr")}
						>
							TR
						</button>
						<button
							type="button"
							className={[styles.langButton, termsLang === "en" && styles.langButtonActive]
								.filter(Boolean)
								.join(" ")}
							onClick={() => setTermsLang("en")}
						>
							EN
						</button>
					</div>
					<div className={styles.formGrid}>
						<FormField label={t("termsCommissioning")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={termsI18n.commissioning?.[termsLang] ?? ""}
								onChange={(e) => setTermValue("commissioning", e.target.value)}
							/>
						</FormField>
						<FormField label={t("termsAssembly")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={termsI18n.assembly?.[termsLang] ?? ""}
								onChange={(e) => setTermValue("assembly", e.target.value)}
							/>
						</FormField>
						<FormField label={t("termsVat")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={termsI18n.vat?.[termsLang] ?? ""}
								readOnly
								disabled
							/>
						</FormField>
						<FormField label={t("termsQuality")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={termsI18n.quality?.[termsLang] ?? ""}
								onChange={(e) => setTermValue("quality", e.target.value)}
							/>
						</FormField>
						<FormField label={t("termsValidity")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={termsI18n.validity?.[termsLang] ?? ""}
								readOnly
								disabled
							/>
						</FormField>
						<FormField label={t("termsWarranty")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={termsI18n.warranty?.[termsLang] ?? ""}
								onChange={(e) => setTermValue("warranty", e.target.value)}
							/>
						</FormField>
						<FormField label={t("termsPayment")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={termsI18n.payment?.[termsLang] ?? ""}
								onChange={(e) => setTermValue("payment", e.target.value)}
							/>
						</FormField>
						<FormField label={t("termsDelivery")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={termsI18n.delivery?.[termsLang] ?? ""}
								onChange={(e) => setTermValue("delivery", e.target.value)}
							/>
						</FormField>
					</div>
				</Card>

				<Card header={t("lineItems")}>
					{alternativeGroups.map((group, groupIdx) => (
						<div key={group} className={styles.alternativeBlock}>
							<div className={styles.alternativeHeader}>
								<span className={styles.alternativeTitle}>
									{t("alternative")} {groupIdx + 1}
								</span>
								<div className={styles.alternativeCurrency}>
									<FormField label={t("currency")}>
										<Select
											options={CURRENCY_OPTIONS}
											value={currencyOfGroup(group)}
											onChange={(e) => changeGroupCurrency(group, e.target.value)}
										/>
									</FormField>
									<FormField label={t("rateDate")}>
										<Input
											type="date"
											value={groupRateDate[group] ?? header.offerDate ?? ""}
											onChange={(e) => void fetchGroupRates(group, e.target.value)}
										/>
									</FormField>
									{groupRateDate[group] &&
										(Object.keys(groupRates[group] ?? {}).length > 0 ? (
											<span className={styles.rateSummary}>
												{["USD", "EUR", "GBP"]
													.filter((c) => groupRates[group]?.[c])
													.map((c) => `${c} ${formatAmount(groupRates[group]![c]!)}`)
													.join(" · ")}
											</span>
										) : (
											<span className={styles.priceWarning}>⚠ {t("noRatesForDate")}</span>
										))}
								</div>
								{alternativeGroups.length > 1 && (
									<Button variant="ghost" size="sm" onClick={() => removeAlternative(group)}>
										<Minus size={16} /> {t("alternative")}
									</Button>
								)}
							</div>
							{lines.map((line, idx) =>
								line.alternativeGroup !== group ? null : (
									<div key={idx} className={styles.lineCard}>
										<div className={styles.lineHeader}>
											<span className={styles.lineBadge}>{idx + 1}</span>
											{lines.length > 1 && (
												<Button
													variant="ghost"
													size="sm"
													className={styles.removeLineBtn}
													onClick={() => removeLine(idx)}
												>
													<Minus size={16} />
												</Button>
											)}
										</div>

										{/* Ürün seçimi */}
										<div className={styles.lineSection}>
											<span className={styles.sectionLabel}>{t("productSelection")}</span>
											<div className={styles.selectRow}>
												<FormField label={t("lineSource")}>
													<Select
														options={[
															{ value: "priceList", label: t("sourcePriceList") },
															{ value: "sap", label: t("sourceSapItem") },
														]}
														value={lineSource[idx] ?? "priceList"}
														onChange={(e) =>
															changeLineSource(idx, e.target.value as "priceList" | "sap")
														}
													/>
												</FormField>
												{(lineSource[idx] ?? "priceList") === "sap" ? (
													<FormField label={t("sourceSapItem")}>
														<SearchableSelect
															key={`src-sap-${idx}`}
															value={directSapKey[idx] ?? ""}
															onChange={(val, option) =>
																handleSelectDirectSapItem(idx, val, option)
															}
															loadOptions={loadDirectSapItems}
															placeholder={t("selectStockItem")}
															clearable
														/>
													</FormField>
												) : (
													<FormField label={t("priceListModel")}>
														<SearchableSelect
															key={`src-pl-${idx}`}
															value={selectedModelKey[idx] ?? ""}
															onChange={(val) => void handleSelectModel(idx, val)}
															loadOptions={loadPriceModels}
															initialOption={modelInitialOptions[idx]}
															placeholder={t("selectPriceListItem")}
															clearable
														/>
													</FormField>
												)}
												{(lineSource[idx] ?? "priceList") === "priceList" &&
													selectedModelKey[idx] && (
														<FormField label={t("sapItem")}>
															<SearchableSelect
																key={`sap-${idx}-${selectedModelKey[idx] ?? ""}`}
																value={selectedSapKey[idx] ?? ""}
																onChange={(val, option) => handleSelectSapItem(idx, val, option)}
																loadOptions={(q) =>
																	loadSapItemsForModel(selectedModelKey[idx] ?? "", q)
																}
																initialOption={sapItemInitialOptions[idx]}
																placeholder={t("selectStockItem")}
																clearable
															/>
														</FormField>
													)}
											</div>
											{(() => {
												const hs = deriveGtip({
													code: line.itemCode,
													name: line.displayDescription || line.itemDescription,
													model: line.priceListModel || selectedModelKey[idx] || null,
												});
												return hs ? (
													<div className={styles.hsCodeRow}>
														<span className={styles.hsCodeLabel}>{t("hsCode")}</span> {hs}
													</div>
												) : null;
											})()}
											{(lineInfo[idx]?.length ?? 0) > 0 && (
												<div className={styles.priceInfo}>
													<span className={styles.priceInfoHint}>{t("clickToAddPrice")}</span>
													{lineInfo[idx]!.map((info) => {
														const applied = appliedInfo[idx]?.[info.label] ?? false;
														const grp = lines[idx]?.alternativeGroup ?? 0;
														const conv = convertPrice(info.price, info.currency ?? null, grp);
														const target = currencyOfGroup(grp);
														return (
															<button
																type="button"
																key={info.label}
																className={[
																	styles.priceInfoItem,
																	applied && styles.priceInfoItemActive,
																]
																	.filter(Boolean)
																	.join(" ")}
																onClick={() => toggleInfoPrice(idx, info)}
															>
																{applied ? "− " : "+ "}
																{info.label}:{" "}
																{formatAmount(conv.converted ? conv.value : info.price)}
																{conv.converted
																	? ` ${target}`
																	: info.currency
																		? ` ${info.currency}`
																		: ""}
															</button>
														);
													})}
												</div>
											)}
											{lineWarning[idx] && (
												<span className={styles.priceWarning}>⚠ {lineWarning[idx]}</span>
											)}
											{Object.keys(linePanels[idx] ?? {}).length > 0 && (
												<div className={styles.panelRow}>
													{["ATS", "Change Over ATS", "CB"]
														.filter((g) => linePanels[idx]?.[g])
														.map((group) => {
															const opts = linePanels[idx]![group]!;
															const cur = linePrices[idx]?.currency ?? "";
															return (
																<div key={group} className={styles.panelGroup}>
																	<label className={styles.panelCheck}>
																		<input
																			type="checkbox"
																			checked={panelChecked[idx]?.[group] ?? false}
																			onChange={() => togglePanelGroup(idx, group)}
																		/>
																		{group}
																	</label>
																	{panelChecked[idx]?.[group] && (
																		<>
																			<Select
																				options={[
																					{ value: "", label: t("selectPrice") },
																					...Object.entries(opts).map(([opt, price]) => ({
																						value: opt,
																						label: `${opt} — ${formatAmount(price)}${cur ? ` ${cur}` : ""}`,
																					})),
																				]}
																				value={panelSelected[idx]?.[group] ?? ""}
																				onChange={(e) =>
																					selectPanelOption(idx, group, e.target.value)
																				}
																			/>
																			<Input
																				type="number"
																				min="1"
																				className={styles.panelQty}
																				title={t("quantity")}
																				placeholder={t("quantity")}
																				value={panelQty[idx]?.[group] ?? "1"}
																				onChange={(e) =>
																					setPanelQtyValue(idx, group, e.target.value)
																				}
																			/>
																			<label className={styles.panelCheck}>
																				<input
																					type="checkbox"
																					checked={panelShow[idx]?.[group] ?? true}
																					onChange={() => togglePanelShow(idx, group)}
																				/>
																				{t("showInOffer")}
																			</label>
																		</>
																	)}
																</div>
															);
														})}
												</div>
											)}
										</div>

										{/* Açıklamalar */}
										<div className={styles.fieldRow}>
											<FormField label={t("itemCode")}>
												<Input
													value={line.itemCode}
													onChange={(e) => updateLine(idx, "itemCode", e.target.value)}
												/>
											</FormField>
											<FormField label={t("itemDescription")}>
												<Input
													value={line.itemDescription}
													onChange={(e) => updateLine(idx, "itemDescription", e.target.value)}
												/>
											</FormField>
											<FormField label={t("displayDescription")}>
												<Input
													value={line.displayDescription}
													onChange={(e) => updateLine(idx, "displayDescription", e.target.value)}
												/>
											</FormField>
										</div>

										{/* Miktar & fiyat */}
										<div className={styles.numbersRow}>
											<FormField label={t("quantity")}>
												<Input
													type="number"
													value={line.quantity}
													onChange={(e) => updateLine(idx, "quantity", e.target.value)}
												/>
											</FormField>
											<FormField label={t("unit")}>
												<Select
													options={[
														{ value: "", label: "—" },
														...unitOptions,
														...(line.unit && !unitOptions.some((o) => o.value === line.unit)
															? [{ value: line.unit, label: line.unit }]
															: []),
													]}
													value={line.unit}
													onChange={(e) => updateLine(idx, "unit", e.target.value)}
												/>
											</FormField>
											<FormField label={t("listPrice")}>
												<Input type="number" value={line.unitPrice} disabled readOnly />
											</FormField>
											<FormField label={t("givenPrice")}>
												<Input
													type="number"
													value={line.givenUnitPrice}
													placeholder={line.unitPrice || undefined}
													onChange={(e) => setGivenPrice(idx, e.target.value)}
												/>
											</FormField>
											<FormField label={t("vatRate")}>
												<SearchableSelect
													value={line.vatGroup}
													options={vatGroups.map((v) => ({
														value: v.code,
														label: `${v.code} — %${v.rate} ${v.name}`,
													}))}
													initialOption={
														line.vatGroup && !vatGroups.some((v) => v.code === line.vatGroup)
															? { value: line.vatGroup, label: line.vatGroup }
															: undefined
													}
													placeholder={t("search")}
													clearable
													onChange={(code) => {
														const g = vatGroups.find((v) => v.code === code);
														setLines((prev) => {
															const next = [...prev];
															if (next[idx])
																next[idx] = {
																	...next[idx],
																	vatGroup: code,
																	...(g ? { vatRate: String(g.rate) } : {}),
																} as LineItem;
															return next;
														});
													}}
												/>
											</FormField>
											<FormField label={t("discountPercent")}>
												<Input
													type="number"
													value={line.discountPercent}
													onChange={(e) => setDiscountPercent(idx, e.target.value)}
												/>
											</FormField>
										</div>

										<FormField label={t("lineDetail")}>
											<textarea
												className={styles.textarea}
												rows={2}
												value={line.lineDetail}
												onChange={(e) => updateLine(idx, "lineDetail", e.target.value)}
											/>
										</FormField>
										<div className={styles.lineFooter}>
											<div className={styles.lineFlags}>
												<label>
													<input
														type="checkbox"
														checked={line.showInPdf}
														onChange={() => toggleLineFlag(idx, "showInPdf")}
													/>
													{t("showInPdf")}
												</label>
												<label>
													<input
														type="checkbox"
														checked={line.showPriceInPdf}
														onChange={() => toggleLineFlag(idx, "showPriceInPdf")}
													/>
													{t("showPriceInPdf")}
												</label>
												<label>
													<input
														type="checkbox"
														checked={line.showDiscountInPdf}
														onChange={() => toggleLineFlag(idx, "showDiscountInPdf")}
													/>
													{t("showDiscountInPdf")}
												</label>
												<label>
													<input
														type="checkbox"
														checked={line.sendDatasheetInEmail}
														onChange={() => toggleLineFlag(idx, "sendDatasheetInEmail")}
													/>
													{t("sendDatasheetInEmail")}
												</label>
											</div>
											<div className={styles.lineTotalWrap}>
												<span className={styles.lineTotalLabel}>{t("lineTotal")}</span>
												<span className={styles.lineTotal}>
													{formatAmount(calcLineTotal(line))}{" "}
													{currencyOfGroup(line.alternativeGroup)}
												</span>
											</div>
										</div>
									</div>
								),
							)}
							<Button
								variant="secondary"
								className={styles.addLineBtn}
								onClick={() => addLine(group)}
							>
								<Plus size={16} />
							</Button>
							<div className={styles.alternativeSubtotal}>
								<span>{t("subtotal")}</span>
								<span>
									{formatAmount(groupTotals(group).subtotal)} {currencyOfGroup(group)}
								</span>
							</div>
						</div>
					))}
					<Button variant="secondary" onClick={addAlternative}>
						<Plus size={16} /> {t("alternative")}
					</Button>
				</Card>

				<Card>
					{alternativeGroups.length === 1 && (
						<div className={styles.totals}>
							<div className={styles.totalRow}>
								<span>{t("subtotal")}</span>
								<span>
									{formatAmount(subtotal)} {header.currency}
								</span>
							</div>
							<div className={styles.totalRow}>
								<span>{t("vatTotal")}</span>
								<span>
									{formatAmount(vatTotal)} {header.currency}
								</span>
							</div>
							<div className={[styles.totalRow, styles.grandTotal].join(" ")}>
								<span>{t("total")}</span>
								<span>
									{formatAmount(total)} {header.currency}
								</span>
							</div>
						</div>
					)}
					<div className={styles.submitRow}>
						<Button
							onClick={submit}
							isLoading={isEdit ? updateOffer.isPending : createOffer.isPending}
						>
							{isEdit ? t("save") : t("create")}
						</Button>
					</div>
				</Card>
			</div>
		</div>
	);
}
